import { combineReducers } from 'redux';

import {
	commandApiSlice,
	coreApiSlice,
	notificationApiSlice,
	orchApiSlice,
	queryApiSlice,
	walletApiSlice,
} from '@/store/api/apiSlice';

import type { IStates } from '../../interface/IReducer';
import type { IActionReducer } from '../../interface/reducer/action';
import type { IAggregationReducer } from '../../interface/reducer/aggregation';
import type { IAuditTrailReducer } from '../../interface/reducer/audit-trail';
import type { IAuthReducer } from '../../interface/reducer/auth';
import type { ICandidateReducer } from '../../interface/reducer/candidate';
import type { ICollationOfficerReducer } from '../../interface/reducer/collation-officer';
import type { IConsistuencyReducer } from '../../interface/reducer/constituency';
import type { ICountryStatesReducer } from '../../interface/reducer/country-state';
import type { IElectionReducer } from '../../interface/reducer/election';
import type { IElectionCycleReducer } from '../../interface/reducer/election-cycle';
import type { IElectionOfficialReducer } from '../../interface/reducer/election-official';
import type { IElectionTypeReducer } from '../../interface/reducer/election-type';
import type { IElectiveCategoryReducer } from '../../interface/reducer/elective-category';
import type { IGeoZoneReducer } from '../../interface/reducer/geo-zone';
import type { IGlobalReducer } from '../../interface/reducer/global';
import type { IIrevReducer } from '../../interface/reducer/irev';
import type { ILGAReducer } from '../../interface/reducer/lga';
import type { INotificationReducer } from '../../interface/reducer/notification';
import type { IPartyReducer } from '../../interface/reducer/party';
import type { IPoolingUnitReducer } from '../../interface/reducer/polling-unit';
import type { IPresidingOfficerReducer } from '../../interface/reducer/presiding-officer';
import type { IQuickActionReducer } from '../../interface/reducer/quick-action';
import type { IReportReducer } from '../../interface/reducer/report';
import type { IResultReducer } from '../../interface/reducer/result';
import type { IResultAnalyticsReducer } from '../../interface/reducer/result-analytics';
import type { IRoleReducer } from '../../interface/reducer/role';
import type { ISearchReducer } from '../../interface/reducer/search';
import type { ISubscriptionReducer } from '../../interface/reducer/subscription';
import type { IUserReducer } from '../../interface/reducer/user';
import type { IVolunteerReducer } from '../../interface/reducer/volunteer';
import type { IWardReducer } from '../../interface/reducer/ward';
import type { IZonesReducer } from '../../interface/reducer/zone';
import type { PrefixKeys } from '../prefix';

interface IAction {
	type: string;
	payload: unknown;
}

const reducer =
	<T>(states: keyof IStates) =>
	(state = {} as T, action: IAction) => {
		if (action.type.toLowerCase().includes((states as string).toLowerCase())) {
			const payload = action.payload;
			if (import.meta.env.DEV && payload && typeof payload === 'object') {
				try {
					Object.freeze(payload);
				} catch (e) {
					/* ignore non-extensible objects */
				}
			}
			return { ...state, [action.type]: payload };
		}
		return state;
	};

const rootreducer = combineReducers<IStates>({
	_aggregation: reducer<IAggregationReducer>('_aggregation'),
	action_: reducer<PrefixKeys<IActionReducer, 'action'>>('action_'),
	_audittrail: reducer<IAuditTrailReducer>('_audittrail'),
	auth_: reducer<PrefixKeys<IAuthReducer, 'auth'>>('auth_'),
	candidate_: reducer<PrefixKeys<ICandidateReducer, 'candidate'>>('candidate_'),
	collationofficer_: reducer<PrefixKeys<ICollationOfficerReducer, 'collationofficer'>>('collationofficer_'),
	constituency_: reducer<PrefixKeys<IConsistuencyReducer, 'constituency'>>('constituency_'),
	state_: reducer<PrefixKeys<ICountryStatesReducer, 'state'>>('state_'),
	election_: reducer<PrefixKeys<IElectionReducer, 'election'>>('election_'),
	electioncycle_: reducer<PrefixKeys<IElectionCycleReducer, 'electioncycle'>>('electioncycle_'),
	electiontype_: reducer<PrefixKeys<IElectionTypeReducer, 'electiontype'>>('electiontype_'),
	electionofficial_: reducer<PrefixKeys<IElectionOfficialReducer, 'electionofficial'>>('electionofficial_'),
	electivecategory_: reducer<PrefixKeys<IElectiveCategoryReducer, 'electivecategory'>>('electivecategory_'),
	_geographicalzone: reducer<IGeoZoneReducer>('_geographicalzone'),
	_irev: reducer<IIrevReducer>('_irev'),
	lga_: reducer<PrefixKeys<ILGAReducer, 'lga'>>('lga_'),
	_notification: reducer<INotificationReducer>('_notification'),
	_party: reducer<IPartyReducer>('_party'),
	_poolingUnit: reducer<IPoolingUnitReducer>('_poolingUnit'),
	_presidingOfficer: reducer<IPresidingOfficerReducer>('_presidingOfficer'),
	_quickAction: reducer<IQuickActionReducer>('_quickAction'),
	_report: reducer<IReportReducer>('_report'),
	_result: reducer<IResultReducer>('_result'),
	resultanalytics_: reducer<PrefixKeys<IResultAnalyticsReducer, 'resultanalytics'>>('resultanalytics_'),
	_role: reducer<IRoleReducer>('_role'),
	_search: reducer<ISearchReducer>('_search'),
	_subscription: reducer<ISubscriptionReducer>('_subscription'),
	_user: reducer<IUserReducer>('_user'),
	_volunteer: reducer<IVolunteerReducer>('_volunteer'),
	ward_: reducer<PrefixKeys<IWardReducer, 'ward'>>('ward_'),
	zone_: reducer<PrefixKeys<IZonesReducer, 'zone'>>('zone_'),
	global: reducer<IGlobalReducer>('global'),
	[walletApiSlice.reducerPath]: walletApiSlice.reducer,
	[coreApiSlice.reducerPath]: coreApiSlice.reducer,
	[queryApiSlice.reducerPath]: queryApiSlice.reducer,
	[commandApiSlice.reducerPath]: commandApiSlice.reducer,
	[orchApiSlice.reducerPath]: orchApiSlice.reducer,
	[notificationApiSlice.reducerPath]: notificationApiSlice.reducer,
});

export default rootreducer;
